import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft } from 'react-icons/fi';
import PlayerAggregator from '../utils/PlayerAggregator';
import './PlayerDetail.css';

const PlayerDetail = ({ battingData, bowlingData }) => {
  const { playerName, statType } = useParams();
  const navigate = useNavigate();
  const [aggregator] = useState(new PlayerAggregator(battingData, bowlingData));
  const [activeTab, setActiveTab] = useState(statType || 'batting');
  const decodedPlayerName = decodeURIComponent(playerName);

  const overallStats = aggregator.getOverallStats(decodedPlayerName, activeTab);
  const teamPerformances = aggregator.aggregateByTeam(decodedPlayerName, activeTab);
  const formatStats = aggregator.aggregateByFormat(decodedPlayerName, activeTab);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [playerName]);

  if (!overallStats) {
    return (
      <div className="player-detail">
        <div className="player-header">
          <button className="back-button" onClick={() => navigate(-1)}>
            <FiArrowLeft /> Back
          </button>
          <h1>{decodedPlayerName}</h1>
        </div>
        <div className="no-data">No statistics found for this player</div>
      </div>
    );
  }

  return (
    <div className="player-detail">
      <div className="player-header">
        <button className="back-button" onClick={() => navigate(-1)}>
          <FiArrowLeft /> Back
        </button>
        <h1>{decodedPlayerName}</h1>
      </div>

      <div className="tabs-section">
        <div className="tabs">
          <button
            className={`tab ${activeTab === 'batting' ? 'active' : ''}`}
            onClick={() => setActiveTab('batting')}
          >
            🏏 Batting
          </button>
          <button
            className={`tab ${activeTab === 'bowling' ? 'active' : ''}`}
            onClick={() => setActiveTab('bowling')}
          >
            🎾 Bowling
          </button>
        </div>
      </div>

      <div className="overall-stats-container">
        <h2>Overall Career Statistics</h2>
        <div className="overall-stats-grid">
          {activeTab === 'batting' ? (
            <>
              <div className="stat-card">
                <div className="stat-label">Matches</div>
                <div className="stat-value">{overallStats.matches}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Innings</div>
                <div className="stat-value">{overallStats.innings}</div>
              </div>
              <div className="stat-card highlight">
                <div className="stat-label">Total Runs</div>
                <div className="stat-value">{overallStats.runs}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Balls</div>
                <div className="stat-value">{overallStats.balls}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Average</div>
                <div className="stat-value">{overallStats.avg}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Strike Rate</div>
                <div className="stat-value">{overallStats.sr}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Fours</div>
                <div className="stat-value">{overallStats.fours}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Sixes</div>
                <div className="stat-value">{overallStats.sixes}</div>
              </div>
            </>
          ) : (
            <>
              <div className="stat-card">
                <div className="stat-label">Matches</div>
                <div className="stat-value">{overallStats.matches}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Overs</div>
                <div className="stat-value">{overallStats.overs.toFixed(1)}</div>
              </div>
              <div className="stat-card highlight">
                <div className="stat-label">Wickets</div>
                <div className="stat-value">{overallStats.wickets}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Runs Conceded</div>
                <div className="stat-value">{overallStats.runsConceded}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Average</div>
                <div className="stat-value">{overallStats.avg}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Economy</div>
                <div className="stat-value">{overallStats.economy}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Maidens</div>
                <div className="stat-value">{overallStats.maidens}</div>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="format-stats-container">
        <h2>Performance by Format</h2>
        <div className="format-cards">
          {Object.entries(formatStats).map(([format, data]) => (
            <div key={format} className="format-card">
              <h3>{format}</h3>
              <div className="format-stats">
                {activeTab === 'batting' ? (
                  <>
                    <div className="format-stat">
                      <span className="label">Matches</span>
                      <span className="value">{data.totals.matches}</span>
                    </div>
                    <div className="format-stat">
                      <span className="label">Runs</span>
                      <span className="value highlight">{data.totals.runs}</span>
                    </div>
                    <div className="format-stat">
                      <span className="label">Avg</span>
                      <span className="value">{data.totals.avg}</span>
                    </div>
                    <div className="format-stat">
                      <span className="label">SR</span>
                      <span className="value">{data.totals.sr}</span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="format-stat">
                      <span className="label">Matches</span>
                      <span className="value">{data.totals.matches}</span>
                    </div>
                    <div className="format-stat">
                      <span className="label">Wickets</span>
                      <span className="value highlight">{data.totals.wickets}</span>
                    </div>
                    <div className="format-stat">
                      <span className="label">Avg</span>
                      <span className="value">{data.totals.avg}</span>
                    </div>
                    <div className="format-stat">
                      <span className="label">Economy</span>
                      <span className="value">{data.totals.economy}</span>
                    </div>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="team-performances">
        <h2>Performance by Team</h2>
        {teamPerformances.map((teamData) => (
          <div key={teamData.team} className="team-section">
            <h3>{teamData.team}</h3>
            <div className="performance-table-container">
              {activeTab === 'batting' ? (
                <table className="performance-table">
                  <thead>
                    <tr>
                      <th>Year</th>
                      <th>Format</th>
                      <th>Matches</th>
                      <th>Innings</th>
                      <th>Runs</th>
                      <th>Avg</th>
                      <th>SR</th>
                      <th>HS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {teamData.performances.map((perf, idx) => (
                      <tr key={idx} className={idx % 2 === 0 ? 'even' : 'odd'}>
                        <td>{perf['Year']}</td>
                        <td>{perf['Format']}</td>
                        <td>{perf['Matches']}</td>
                        <td>{perf['Innings']}</td>
                        <td className="highlight">{perf['Runs']}</td>
                        <td>{perf['Avg']}</td>
                        <td>{perf['SR']}</td>
                        <td>{perf['HS']}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <table className="performance-table">
                  <thead>
                    <tr>
                      <th>Year</th>
                      <th>Format</th>
                      <th>Matches</th>
                      <th>Overs</th>
                      <th>Runs Conceded</th>
                      <th>Wickets</th>
                      <th>Avg</th>
                      <th>Economy</th>
                    </tr>
                  </thead>
                  <tbody>
                    {teamData.performances.map((perf, idx) => (
                      <tr key={idx} className={idx % 2 === 0 ? 'even' : 'odd'}>
                        <td>{perf['Year']}</td>
                        <td>{perf['Format']}</td>
                        <td>{perf['Matches']}</td>
                        <td>{perf['Overs']}</td>
                        <td>{perf['Runs Conceded']}</td>
                        <td className="highlight">{perf['Wickets']}</td>
                        <td>{perf['Avg']}</td>
                        <td>{perf['Economy']}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PlayerDetail;
